export const ENGLISH_OPERATOR_SURFACE = "english_beta";
export const OPERATOR_SURFACE_STORAGE_KEY = "operator_surface";
export const MAIN_SALESBREAKER_HOSTS = new Set(["salesbreaker.jp", "www.salesbreaker.jp", "localhost"]);

export const JAPAN_GTM_ASSISTANT_URL =
  "https://chatgpt.com/g/g-6a0a2c7661888191a9b7a38cbd622308-japan-gtm-assistant-by-salesbreaker";

export const SALESBREAKER_ENGLISH_GUIDE_URL =
  "https://chatgpt.com/g/g-6a0a2d7a85d48191be50758da72faaf0-salesbreaker-english-guide";

export const ENGLISH_BETA_LEGAL_SUMMARY_URL = "/pages/english-beta-legal.html";
export const ENGLISH_BETA_TERMS_URL = "/pages/terms-en.html";
export const ENGLISH_BETA_PRIVACY_URL = "/pages/privacy-en.html";
export const ENGLISH_BETA_SCTA_URL = "/pages/scta-en.html";

export const ENGLISH_BETA_VISIBLE_PATHS = new Set([
  "/mypage",
  "/settings",
  "/settings/booking",
  "/templates",
  "/client-sales-lists",
  "/list-templates",
  "/send",
  "/history",
  "/click-logs",
  "/deals",
  "/customer-ledger",
  "/marketing/leads",
  "/marketing/email",
  "/marketing/scenarios",
  "/marketing/forms",
  "/marketing/lps",
  "/marketing/presentations",
  "/marketing/tracking",
  "/users",
  "/invoices",
  "/help",
  "/account",
  "/api-keys-en",
]);

export const ENGLISH_BETA_MENU_LABELS = {
  "/mypage": "Dashboard",
  "/settings": "Company Profile & Suppression",
  "/settings/booking": "Booking",
  "/templates": "Templates",
  "/client-sales-lists": "Custom Lists",
  "/list-templates": "Recipient Lists",
  "/send": "Start Outreach",
  "/history": "Execution History",
  "/click-logs": "Engagement Events",
  "/deals": "Engagement Pipeline",
  "/customer-ledger": "Customer Ledger (CRM)",
  "/marketing/leads": "Lead Management",
  "/marketing/email": "Email Campaigns",
  "/marketing/scenarios": "Scenario Campaigns",
  "/marketing/forms": "Form Builder",
  "/marketing/lps": "Landing Pages",
  "/marketing/presentations": "Presentations",
  "/marketing/tracking": "Marketing Settings",
  "/users": "User Management",
  "/invoices": "Billing History",
  "/help": "Help & Support",
  "/account": "Account",
  "/api-keys-en": "API Keys / AI Integration",
};

export const ENGLISH_BETA_GROUP_LABELS = {
  dashboard: "Dashboard",
  preparation: "Setup",
  sending: "Execution",
  management: "Observability",
  marketing: "Marketing",
  account: "Account",
  api_keys: "API Keys",
};

export const normalizeAppPath = (path = "") => {
  if (!path) return "";
  const normalized = path === "/app" ? "/" : path.replace(/^\/app(?=\/)/, "");
  return normalized === "/" ? "/mypage" : normalized;
};

export const readOperatorSurfaceFromSearch = (search = "") => {
  try {
    const params = new URLSearchParams(search || "");
    return params.get("surface") || params.get("operator_surface") || "";
  } catch (error) {
    return "";
  }
};

export const isMainSalesBreakerHost = (hostname = "") => {
  const currentHostname =
    hostname || (typeof window !== "undefined" ? window.location.hostname : "");
  return MAIN_SALESBREAKER_HOSTS.has(currentHostname);
};

export const rememberOperatorSurfaceFromLocation = (search = "") => {
  if (typeof window === "undefined") return "";
  const surface = readOperatorSurfaceFromSearch(search || window.location.search);
  if (surface === ENGLISH_OPERATOR_SURFACE) {
    window.localStorage.setItem(OPERATOR_SURFACE_STORAGE_KEY, ENGLISH_OPERATOR_SURFACE);
    return ENGLISH_OPERATOR_SURFACE;
  }
  return window.localStorage.getItem(OPERATOR_SURFACE_STORAGE_KEY) || "";
};

export const clearEnglishOperatorSurface = () => {
  if (typeof window === "undefined") return;
  const candidateKeys = [
    OPERATOR_SURFACE_STORAGE_KEY,
    "operatorSurface",
    "operator_surface_mode",
    "checkout_surface",
    "surface",
  ];

  candidateKeys.forEach((key) => {
    try {
      if (key === OPERATOR_SURFACE_STORAGE_KEY || window.localStorage.getItem(key) === ENGLISH_OPERATOR_SURFACE) {
        window.localStorage.removeItem(key);
      }
    } catch (error) {
      // Ignore storage access errors in restricted browser contexts.
    }
  });
};

export const isEnglishOperatorMode = () => {
  if (typeof window === "undefined") return false;
  const surface = rememberOperatorSurfaceFromLocation(window.location.search);
  return surface === ENGLISH_OPERATOR_SURFACE;
};

export const isEnglishBetaSignupSource = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === ENGLISH_OPERATOR_SURFACE ||
    normalized.startsWith(`${ENGLISH_OPERATOR_SURFACE}_`) ||
    normalized.startsWith(`inquiry_ref:${ENGLISH_OPERATOR_SURFACE}`);
};

export const getOperatorSurfaceQuery = () =>
  isEnglishOperatorMode() ? `?surface=${ENGLISH_OPERATOR_SURFACE}` : "";

export const withOperatorSurface = (path) =>
  isEnglishOperatorMode() ? `${path}${path.includes("?") ? "&" : "?"}surface=${ENGLISH_OPERATOR_SURFACE}` : path;

export const isEnglishBetaVisiblePath = (path) =>
  ENGLISH_BETA_VISIBLE_PATHS.has(normalizeAppPath(path));

export const getEnglishBetaMenuLabel = (path, fallback) =>
  ENGLISH_BETA_MENU_LABELS[normalizeAppPath(path)] || fallback;

export const getEnglishBetaGroupLabel = (group, fallback) =>
  ENGLISH_BETA_GROUP_LABELS[group?.id] || fallback;

export const getEnglishPlanName = (planName = "", planId = "") => {
  const source = `${planName || ""} ${planId || ""}`.toLowerCase();
  if (!source.trim() || source.includes("確認中")) return "Checking...";
  if (source.includes("trial") || source.includes("トライアル")) return "Trial";
  if (source.includes("free") || source.includes("無料")) return "Free";
  if (source.includes("starter") || source.includes("スターター")) return "Starter";
  if (source.includes("standard") || source.includes("スタンダード")) return "Standard";
  if (source.includes("premium") || source.includes("プレミアム")) return "Premium";
  return planId ? planId.replace(/_/g, " ") : planName;
};

export const getEnglishSubscriptionStatus = (status = "") => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "trialing") return "Trial";
  if (normalized === "active") return "Active";
  if (normalized === "past_due") return "Payment Review";
  if (normalized === "incomplete") return "Checkout Incomplete";
  if (normalized === "canceled") return "Canceled";
  return status || "Unknown";
};

export const formatJstDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.toLocaleString("en-US", { timeZone: "Asia/Tokyo" })} JST`;
};

export const englishStatusLabel = (status, errorDetails = "") => {
  const normalized = String(status || "").toLowerCase();
  const details = String(errorDetails || "").toLowerCase();

  if (normalized === "success") return "Submitted";
  if (normalized === "needs_review" || normalized.includes("review")) return "Needs Review";
  if (normalized === "suppressed" || normalized === "ng" || details.includes("ng")) return "Suppressed";
  if (
    normalized === "no_form_found" ||
    normalized === "dead_url" ||
    normalized === "wrong_form_type" ||
    normalized === "sales_rejected" ||
    details.includes("フォームを発見できませんでした") ||
    details.includes("no_form_found") ||
    details.includes("予約フォーム") ||
    details.includes("応募フォーム")
  ) {
    return "Not Eligible";
  }
  if (normalized.startsWith("fail") || normalized === "logic_failed" || normalized === "gpt_failed") {
    return "Failed";
  }
  if (!normalized) return "Not Started";
  return "Not Eligible";
};
